/**
 * Client-side i18next bootstrap for Emburse Spend.
 *
 * Initializes a single shared i18next instance (no ICU) that every React entry
 * point — the main SPA (`App.tsx`) and the standalone route wrappers — shares via
 * `I18nextProvider`. The active locale is read from `window.LOCALE`, which the
 * server injects during bootstrap, and resolved against the locales we support.
 *
 * Formatting (currency, date) delegates to shared helpers backed by the native
 * `Intl` APIs keyed to the active language.
 */
import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { AbacusCurrency } from '../../../shared/helpers/abacusCurrency';
import { localizedFormatDate } from '../../../shared/helpers/abacusTimeFormatter';
import * as enCommonImport from '../../../shared/i18n/locales/en/common.json';
import { DEFAULT_LOCALE, resolveSupportedLocale } from '../../../shared/i18n/supportedLocales';

export { AbacusCurrency } from '../../../shared/helpers/abacusCurrency';
export type { LocalizedStringOptions } from '../../../shared/helpers/abacusCurrency';
export { localizedFormatDate } from '../../../shared/helpers/abacusTimeFormatter';

// Interop-safe: webpack/babel exposes the catalog under `.default`, while the
// ts-jest (CommonJS) transform returns the object directly.
const enCommon = (enCommonImport as { default?: Record<string, unknown> }).default ?? enCommonImport;

export interface CurrencyLocalizationFormatOptions {
  /** ISO 4217 currency code. Defaults to `USD`. */
  currency: string;
  /** When false (default), `value` is in minor units (cents). Pass `true` for major units (e.g. dollars). */
  isAmountMajorUnits?: boolean;
}

/** Options passed to the i18next interpolation `format` callback. */
export interface LocalizationFormatOptions {
  currency_options?: CurrencyLocalizationFormatOptions;
}

/**
 * i18next interpolation formatter. Supports `currency` and `date` format hints
 * (e.g. `{{amount, currency}}`) using shared localized formatters.
 * Unknown formats return the value coerced to a string.
 *
 * @param value - the interpolated value
 * @param formatHint - the format name from the placeholder (e.g. `currency`)
 * @param lng - the active language
 * @param options - interpolation options (e.g. `{ currency_options: { currency: 'CAD' } }`)
 */
function format(
  value: any,
  formatHint: string | undefined,
  locale_param: string | undefined,
  options: LocalizationFormatOptions & Record<string, unknown> = {}
): string {
  const locale = resolveSupportedLocale(locale_param ?? DEFAULT_LOCALE);

  switch (formatHint) {
    case 'currency': {
      const { currency_options } = options;

      if (!currency_options) {
        throw new Error('currency_options are required');
      }
      const { currency, isAmountMajorUnits = false } = currency_options;
      return new AbacusCurrency(Number(value), currency, isAmountMajorUnits, undefined, locale).toLocalizedString();
    }
    case 'date':
      return localizedFormatDate(value, locale);
    default:
      return value as string;
  }
}

// `window.LOCALE` is injected by the server bootstrap.
const injectedLocale = (window as Window & { LOCALE?: string }).LOCALE; // NOSONAR: `window` is intentional for this browser entry point
const locale = resolveSupportedLocale(injectedLocale);

// A dedicated instance (rather than the global singleton) shared by every entry
// point via `I18nextProvider`.
const i18n = createInstance();

i18n.use(initReactI18next).init({
  lng: locale,
  // `en-US` resolves to the base-language `en` catalog until region catalogs land.
  fallbackLng: DEFAULT_LOCALE,
  ns: ['common'],
  defaultNS: 'common',
  resources: {
    en: { common: enCommon }
  },
  interpolation: {
    // React escapes rendered output already, so i18next must not double-escape.
    escapeValue: false,
    format
  },
  returnNull: false
});

export default i18n; // eslint-disable-line no-restricted-syntax
